import { ConsentManagerState } from './consent-manager-state'
import { debounce } from '../util/helpers'
import {
  addPlaceholder,
  PLACEHOLDER_CLASS,
  PLACEHOLDER_CONTENT_CLASS,
  removePlaceholder,
} from './placeholders'

const LS_KEY: string = 'ob-consent'

export interface Consent {
  service: CM.Config.ProcessedService
  timestamp: number
}

// For logging purposes
const CONSENT_REVOKE = 'revoke'
const CONSENT_GIVE = 'give'
type ConsentAction = typeof CONSENT_REVOKE | typeof CONSENT_GIVE

export class ConsentManager {
  #clientId: string
  #blocker: CM.Blocking.Blocker

  state: ConsentManagerState
  ui?: {
    openManager: () => void
  }

  constructor({
    clientId,
    config,
    blocker,
  }: {
    clientId: string
    config: CM.Config.RawConfig
    blocker: CM.Blocking.Blocker
  }) {
    this.#clientId = clientId
    this.state = new ConsentManagerState(config)
    this.#blocker = blocker

    this.state.services.forEach(async service => {
      await this.runServiceRelatedHook(service, 'onInit')

      if (this.hasOutdatedConsent(service.id)) {
        await this.revokeConsent(service.id)
      }

      service.checked = this.hasConsent(service.id)
    })
  }

  get clientId() {
    return this.#clientId
  }

  /**
   * Finds all elements that were blocked on the server side.
   */
  findServerSideBlockedElements() {
    let blockedElements = [
      ...document.querySelectorAll<CM.Blocking.BlockableElement>(
        'script[type="text/blocked-script"][data-service-id], iframe[data-src][data-service-id], link[data-href][data-service-id], [data-service-id][data-wrap-in-placeholder]',
      ),
    ]

    for (let element of blockedElements) {
      this.#blocker.registerBlockedServiceElement(element.dataset.serviceId!, element)
    }
  }

  /**
   * Returns a list of the users consents.
   */
  getConsents(): Consent[] {
    return JSON.parse(localStorage.getItem(LS_KEY) ?? '[]') as Consent[]
  }

  /**
   * Returns true if the given service exists.
   * @param serviceId
   */
  hasService(serviceId: string): boolean {
    return this.getService(serviceId) !== undefined
  }

  /**
   * Returns the service object matching the given serviceId.
   * @param serviceId
   */
  getService(serviceId: string): CM.Config.ProcessedService | undefined {
    return this.state.services.find(service => service.id === serviceId)
  }

  /**
   * Get service versions (mapping of service id to service version)
   */
  getServiceVersions(): Record<string, string> {
    let sortedServices = [...this.state.services].sort((a, b) => a.id.localeCompare(b.id))
    let serviceEntries = sortedServices.map(service => [service.id, service.version])
    return Object.fromEntries(serviceEntries)
  }

  /**
   * Adds placeholders for blocked elements for which the user has not given consent.
   */
  addPlaceholders() {
    const blockedElements = document.querySelectorAll<HTMLElement>(
      '[data-service-id]:is(iframe[data-src], [data-wrap-in-placeholder])',
    )
    blockedElements.forEach(blockedElement => {
      let serviceId = blockedElement.dataset.serviceId
      if (!serviceId) return

      if (this.hasConsent(serviceId)) {
        blockedElement.setAttribute('data-has-consent', 'true')
        this.unblock(serviceId)
      } else {
        addPlaceholder(blockedElement)
      }
    })
  }

  /**
   * Persists the consent for the given service.
   * Returns true if successful and false otherwise.
   *
   * @param serviceId
   */
  giveConsent(serviceId: string): boolean {
    const consents = this.getConsents()
    const service = this.getService(serviceId)
    if (!service) return false

    service.checked = true

    let consent: Consent = {
      service: service,
      timestamp: new Date().getTime(),
    }
    consents.push(consent)

    localStorage.setItem(LS_KEY, JSON.stringify(consents))
    this.unblock(serviceId).then(() => {
      this.runServiceRelatedHook(service, 'onGiveConsent')
    })
    this.log(CONSENT_GIVE, service)

    window.dispatchEvent(new CustomEvent('ob:consent-given', { detail: consent }))

    return true
  }

  /**
   * Gives consent for all services in the given group.
   *
   * @param groupId
   */
  giveConsentForGroup(groupId: string) {
    let group = this.state.config.groups.find(group => group.id === groupId)
    if (!group) return false

    for (let service of group.services) {
      this.giveConsent(service.id)
    }
  }

  /**
   * Revokes consent for all services in the given group.
   *
   * @param groupId
   */
  revokeConsentForGroup(groupId: string) {
    let group = this.state.config.groups.find(group => group.id === groupId)
    if (!group) return false
    for (let service of group.services) {
      this.revokeConsent(service.id)
    }
  }

  /**
   * Sets consent for all services in the given group.
   *
   * @param groupId
   * @param hasConsent Consent state to set
   */
  setConsentForGroup(group: string, hasConsent: boolean) {
    return hasConsent ? this.giveConsentForGroup(group) : this.revokeConsentForGroup(group)
  }

  /**
   * Gives or revokes the consent for the given service vased on the
   * hasConsent boolean value.
   * @param serviceId
   * @param hasConsent
   */
  setConsent(serviceId: string, hasConsent: boolean) {
    hasConsent ? this.giveConsent(serviceId) : this.revokeConsent(serviceId)
  }

  toggleConsent(serviceId: string) {
    if (this.hasConsent(serviceId)) {
      this.revokeConsent(serviceId)
    } else {
      this.giveConsent(serviceId)
    }
  }

  /**
   * Revokes consent for the given service.
   *
   * @param serviceId
   */
  revokeConsent(serviceId: string): void {
    //console.debug('REVOKE consent for service ' + serviceId)
    let allConsents = this.getConsents()
    let revokedService = this.getService(serviceId)

    if (!revokedService) return
    if (revokedService.required) return

    revokedService.checked = false
    let remainingConsents = allConsents.filter(consent => consent.service.id !== serviceId)
    localStorage.setItem(LS_KEY, JSON.stringify(remainingConsents))
    this.#blocker.block(serviceId)
    this.log(CONSENT_REVOKE, revokedService)
    this.runServiceRelatedHook(revokedService, 'onRevokeConsent')
  }

  /**
   * Revokes all consents.
   */
  revokeAllConsents(): void {
    const consents = this.getConsents()
    for (let consent of consents) {
      this.revokeConsent(consent.service.id)
    }
  }

  giveAllConsents(): void {
    this.state.config.groups.forEach(group => {
      this.giveConsentForGroup(group.id)
    })
  }

  /**
   * Returns true if the user has given consent for the given service but the service version has changed.
   * @param serviceId
   */
  hasOutdatedConsent(serviceId: string): boolean {
    let storedConsent = this.getConsent(serviceId)
    if (!storedConsent) return false

    let service = this.getService(serviceId)
    if (!service) {
      if (DEV_MODE) console.warn('Service does not exist: %s', serviceId)
      return false
    }

    let currentServiceVersion = service.version
    return storedConsent.service.version !== currentServiceVersion
  }

  /**
   * Returns true if the user has given consent for the given service.
   * @param serviceId
   */
  hasConsent(serviceId: string): boolean {
    // If the stored consent serviceVersion does not match the current serviceVersion the consent must be invalidated
    let service = this.getService(serviceId)

    if (!service) {
      if (DEV_MODE) console.warn('Service does not exist: %s', serviceId)
      return false
    }

    if (service.required) return true

    let storedConsent = this.getConsent(serviceId)
    if (!storedConsent) return false

    let currentServiceVersion = service.version
    let consentValid = storedConsent.service.version === currentServiceVersion
    return storedConsent && consentValid
  }

  /**
   * Returns the locally stored consent object.
   * @param serviceId
   */
  getConsent(serviceId: string): Consent | false {
    let consents = this.getConsents()
    let consent = consents.find(consent => consent.service.id === serviceId)
    if (!consent) return false
    return consent
  }

  /**
   * Wait for the user to give consent for the given service.
   * @param serviceId
   */
  awaitConsent(serviceId: string): Promise<Consent> {
    return new Promise(resolve => {
      if (this.hasConsent(serviceId)) {
        resolve(this.getConsent(serviceId) as Consent)
      } else {
        let consentGivenListener = (event: CustomEvent<Consent>) => {
          if (event.detail.service.id === serviceId) {
            window.removeEventListener('ob:consent-given', consentGivenListener)
            resolve(event.detail)
          }
        }
        window.addEventListener('ob:consent-given', consentGivenListener)
      }
    })
  }

  hasConsentForGroup(group: CM.Config.Group<CM.Config.ProcessedService>) {
    return group.services.every(service => service.checked)
  }

  #logStack: Array<{
    client_id: string
    consent_type: ConsentAction
    service_id: string
    service_version: string
  }> = []
  #handleLogStack = debounce(() => {
    if (this.#logStack.length === 0) return

    // Do a post request to http://templatesownbit.local/wp-json/consent-manager/log-consent/ to log consent
    const consents = [...this.#logStack]
    this.#logStack = []

    fetch(
      new URL(
        window.obConsentManagerFrontendEnv.endpoints.log,
        window.obConsentManagerFrontendEnv.restBase,
      ),
      {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(consents),
      },
    ).catch(error => {
      console.error('Failed to log consent', error)
    })
  }, 50)

  async log(consentType: ConsentAction, service: CM.Config.Service) {
    if (DEV_SERVER) {
      console.debug('log', {
        client_id: this.clientId,
        consent_type: consentType,
        service_id: service.id,
        service_version: service.version,
      })
      return
    } else {
      this.#logStack.push({
        client_id: this.clientId,
        consent_type: consentType,
        service_id: service.id,
        service_version: service.version,
      })
      this.#handleLogStack()
    }
  }

  async evalServiceRelatedCode(code: string, service: CM.Config.ProcessedService): Promise<void> {
    const AsyncFunction = (async () => {}).constructor

    try {
      await AsyncFunction('service', `"use strict"; ${code}`)(service)
    } catch (error) {
      console.error('Failed to run service-related user code:', error)
    }
  }

  /**
   * Run service related code
   * @param service The service to run the hook for
   */
  async runServiceRelatedHook(
    service: CM.Config.ProcessedService,
    hook: 'onInit' | 'onGiveConsent' | 'onRevokeConsent' | 'onUnblock',
  ) {
    if (service.runCode[hook].trim().length > 0) {
      // We could use toRaw() here, but that would import _all_ of Vue into the frontend bundle,
      // so we just clone through JSON serialization instead
      const rawService: typeof service = JSON.parse(JSON.stringify(service))
      await this.evalServiceRelatedCode(service.runCode[hook], rawService)
    }
  }

  async unblock(serviceId: string): Promise<void> {
    const service = this.state.services.find(service => service.id === serviceId)
    if (!service) return

    if (this.#blocker.unblockedServices.has(serviceId)) return

    this.#blocker.unblockedServices.add(serviceId)
    const elements = this.#blocker.blockedServices.get(serviceId) ?? []
    for (let element of elements) {
      delete element.dataset.blocked
      switch (element.nodeName) {
        case 'SCRIPT':
          await this.unblockScript(element).catch(error => {
            console.warn('Could not unblock service script', serviceId, error)
          })
          break

        case 'IFRAME':
          // While <iframe>s are awaitable, there is no need to unblock them in order
          this.unblockIframe(element)
          break

        case 'LINK':
          // While <link>s are awaitable, there is no need to unblock them in order
          this.unblockLink(element)
          break

        case 'IMG':
          // While <img>s are awaitable, there is no need to unblock them in order
          this.unblockImg(element)
          break

        default:
          this.unblockElement(element)
      }
    }

    await this.runServiceRelatedHook(service, 'onUnblock')
  }

  /**
   * Unblock a <script> by re-applying its 'src'/'type' attributes
   */
  unblockScript(element: HTMLElement): Promise<void> {
    let src = element.getAttribute('src')
    if (src) {
      // External script

      const loaded = new Promise<void>((resolve, reject) => {
        let onLoad = () => {
          removeListeners()
          resolve()
        }
        let onError = () => {
          removeListeners()
          reject()
        }
        element.addEventListener('load', onLoad)
        element.addEventListener('error', onError)
        let removeListeners = () => {
          element.removeEventListener('load', onLoad)
          element.removeEventListener('error', onError)
        }
      })

      element.removeAttribute('src')
      if (element.dataset.type) {
        this.#blocker.nativeSetAttribute(element, 'type', element.dataset.type)
      } else {
        element.removeAttribute('type')
      }
      // Re-apply src to trigger loading
      this.#blocker.nativeSetAttribute(element, 'src', src)

      return loaded
    } else {
      // Inline script
      if (element.dataset.type) {
        this.#blocker.nativeSetAttribute(element, 'type', element.dataset.type)
      } else {
        element.removeAttribute('type')
      }
      let duplicate = element.cloneNode(true) as HTMLScriptElement
      element.insertAdjacentElement('afterend', duplicate)
      element.remove()

      return Promise.resolve()
    }
  }

  /**
   * Unblock an arbitrary HTML element by removing its placeholder (if exists)
   */
  unblockElement(element: HTMLElement) {
    removePlaceholder(element)
  }

  /**
   * Unblock an <iframe> by re-applying its 'src' attribute and removing the placeholder
   */
  unblockIframe(element: HTMLElement): Promise<void> {
    return new Promise(resolve => {
      let src = element.dataset.src ?? ''
      element.removeAttribute('tabindex')
      element.addEventListener(
        'load',
        () => {
          this.unblockElement(element)
          element.removeAttribute('data-src')
          resolve()
        },
        { once: true },
      )
      this.#blocker.nativeSetAttribute(element, 'src', src)
    })
  }

  /**
   * Unblock a <link> by re-applying its 'href' attribute
   */
  unblockLink(element: HTMLElement): Promise<void> {
    return new Promise(resolve => {
      element.addEventListener('load', () => resolve(), { once: true })

      let href = element.dataset.href ?? ''
      this.#blocker.nativeSetAttribute(element, 'href', href)
      element.removeAttribute('data-href')
    })
  }

  /**
   * Unblock an <img> by re-applying its 'src' attribute
   */
  unblockImg(element: HTMLElement): Promise<void> {
    return new Promise(resolve => {
      element.addEventListener('load', () => resolve(), { once: true })

      let src = element.dataset.src ?? ''
      this.#blocker.nativeSetAttribute(element, 'src', src)
      element.removeAttribute('data-src')
    })
  }

  /*
   * Automatically unblocks services that the user gave consent for.
   */
  autoUnblock() {
    let requiredServices = this.state.services.filter(service => service.required)
    for (let service of requiredServices) {
      this.unblock(service.id)
    }

    let consents = this.getConsents()
    consents.forEach(consent => {
      if (consent.service.required) return
      if (!this.hasConsent(consent.service.id)) return
      this.unblock(consent.service.id)
    })
  }
}

export function createGlobalConsentManager() {
  let consentManager = new ConsentManager({
    clientId: window.obConsentManagerClientId,
    config: window.obConsentManagerConfig,
    blocker: window.obConsentManagerBlocker,
  })

  window.obConsentManager = consentManager
  window.dispatchEvent(new CustomEvent('ob:consent-manager-ready'))

  return consentManager
}
